import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload, X, Star, Loader2, Camera, ImageIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useAssetImages, useUploadAssetImage, useSetPrimaryImage, useDeleteAssetImage } from '@/api/hooks/useAssets';
import { AssetImage } from '@/types/asset.types';
import { toast } from 'sonner';

interface AssetImageManagerProps {
  assetId: string;
  maxImages?: number;
  maxSize?: number; // in bytes
  disabled?: boolean;
  className?: string;
}

export function AssetImageManager({
  assetId,
  maxImages = 10,
  maxSize = 5 * 1024 * 1024, // 5MB default
  disabled = false,
  className,
}: AssetImageManagerProps) {
  const { t } = useTranslation(['assets', 'common']);
  const [deleteImage, setDeleteImage] = useState<AssetImage | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const { data: images = [], isLoading } = useAssetImages(assetId);
  const uploadMutation = useUploadAssetImage();
  const setPrimaryMutation = useSetPrimaryImage();
  const deleteMutation = useDeleteAssetImage();

  const handleFiles = useCallback(
    async (files: FileList | null) => {
      if (!files || files.length === 0) return;

      const filesArray = Array.from(files);

      // Validate file count
      if (images.length + filesArray.length > maxImages) {
        toast.error(t('images.maxImagesError', { count: maxImages }));
        return;
      }

      // Validate each file
      for (const file of filesArray) {
        if (!file.type.startsWith('image/')) {
          toast.error(t('images.notImageError', { name: file.name }));
          return;
        }
        if (file.size > maxSize) {
          toast.error(t('images.fileTooLargeError', { name: file.name, size: Math.round(maxSize / 1024 / 1024) }));
          return;
        }
      }

      // Upload files one by one
      setIsUploading(true);
      try {
        for (const file of filesArray) {
          await uploadMutation.mutateAsync({ assetId, file });
        }
        toast.success(t('images.uploadSuccess'));
      } catch {
        toast.error(t('images.uploadError'));
      } finally {
        setIsUploading(false);
      }
    },
    [assetId, images.length, maxImages, maxSize, t, uploadMutation]
  );

  const handleClick = () => {
    if (!disabled && images.length < maxImages && !isUploading) {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      input.multiple = true;
      input.onchange = (e) => {
        handleFiles((e.target as HTMLInputElement).files);
      };
      input.click();
    }
  };

  const handleCapture = () => {
    if (!disabled && images.length < maxImages && !isUploading) {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      input.capture = 'environment';
      input.onchange = (e) => {
        handleFiles((e.target as HTMLInputElement).files);
      };
      input.click();
    }
  };

  const handleSetPrimary = async (image: AssetImage) => {
    if (image.isPrimary || disabled) return;
    try {
      await setPrimaryMutation.mutateAsync({ assetId, imageId: image.imageId });
      toast.success(t('images.primarySuccess'));
    } catch {
      toast.error(t('images.primaryError'));
    }
  };

  const handleDelete = async () => {
    if (!deleteImage) return;
    try {
      await deleteMutation.mutateAsync({ assetId, imageId: deleteImage.imageId });
      toast.success(t('images.deleteSuccess'));
    } catch {
      toast.error(t('images.deleteError'));
    } finally {
      setDeleteImage(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className={cn('space-y-4', className)}>
      {/* Image grid */}
      <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-5">
        {images.map((image) => (
          <div key={image.imageId} className="group relative aspect-square">
            <img
              src={image.imageUrl}
              alt={image.name}
              className="h-full w-full rounded-lg object-cover ring-1 ring-border"
            />
            {/* Primary badge */}
            {image.isPrimary && (
              <div className="absolute left-1 top-1 rounded bg-primary px-1.5 py-0.5 text-[10px] font-medium text-primary-foreground">
                {t('images.primary')}
              </div>
            )}
            {/* Overlay with actions */}
            {!disabled && (
              <div className="absolute inset-0 flex items-center justify-center gap-1 rounded-lg bg-black/60 opacity-0 transition-opacity group-hover:opacity-100">
                {/* Set primary button */}
                <Button
                  type="button"
                  variant={image.isPrimary ? 'secondary' : 'ghost'}
                  size="icon"
                  className="h-8 w-8 text-white hover:bg-white/20"
                  onClick={() => handleSetPrimary(image)}
                  disabled={image.isPrimary || setPrimaryMutation.isPending}
                  title={t('images.setPrimary')}
                >
                  <Star className={cn('h-4 w-4', image.isPrimary && 'fill-yellow-400 text-yellow-400')} />
                </Button>
                {/* Delete button */}
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-white hover:bg-destructive/80"
                  onClick={() => setDeleteImage(image)}
                  disabled={deleteMutation.isPending}
                  title={t('images.delete')}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        ))}

        {/* Add button */}
        {images.length < maxImages && !disabled && (
          <button
            type="button"
            onClick={handleClick}
            disabled={isUploading}
            className="flex aspect-square items-center justify-center rounded-lg border-2 border-dashed border-muted-foreground/25 transition-colors hover:border-primary/50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isUploading ? (
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            ) : (
              <Upload className="h-6 w-6 text-muted-foreground" />
            )}
          </button>
        )}
      </div>

      {/* Empty state */}
      {images.length === 0 && !isUploading && (
        <div className="flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-muted-foreground/25 py-8">
          <ImageIcon className="h-10 w-10 text-muted-foreground/50" />
          <p className="mt-2 text-sm text-muted-foreground">{t('images.noImages')}</p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleClick}
            disabled={disabled}
            className="mt-3"
          >
            <Upload className="mr-2 h-4 w-4" />
            {t('images.upload')}
          </Button>
        </div>
      )}

      {/* Capture button for mobile */}
      {images.length > 0 && images.length < maxImages && !disabled && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleCapture}
          disabled={isUploading}
          className="w-full sm:w-auto"
        >
          <Camera className="mr-2 h-4 w-4" />
          {t('images.takePhoto')}
        </Button>
      )}

      {/* Helper text */}
      <p className="text-xs text-muted-foreground">
        {images.length}/{maxImages} {t('images.imagesCount')} | {t('images.maxSize', { size: Math.round(maxSize / 1024 / 1024) })}
      </p>

      {/* Delete confirmation dialog */}
      <AlertDialog open={!!deleteImage} onOpenChange={(open) => !open && setDeleteImage(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('images.deleteConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('images.deleteConfirmDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common:buttons.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : null}
              {t('common:buttons.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
